import React, { useState, memo, useCallback, useEffect } from "react";
import { useReleases } from "../context/ReleasesContext";
import { Release } from "../types";

interface ReleaseCardProps {
  release: Release;
  labelId: number;
  labelName: string;
}

const ReleaseCard: React.FC<ReleaseCardProps> = memo(({ release, labelId, labelName }) => {
  const { getSessionToggle, toggleListen, loadingReleaseId, error } = useReleases();
  const [isVisible, setIsVisible] = useState(false);

  // Determine listened state: session toggle overrides server data
  const isListened = release.id
    ? (getSessionToggle(release.id) ?? release.listened ?? false)
    : false;
  const isLoading = release.id && loadingReleaseId === release.id;

  // Add a fade-in effect when the component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleToggle = useCallback(async () => {
    if (!release.id) return; // Safety check
    await toggleListen(release.id, isListened);
  }, [release.id, toggleListen, isListened]);

  return (
    <div className={`w-36 sm:w-44 flex-shrink-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="relative">
        <a 
          href={release.release_url} 
          target="_blank"
          rel="noopener noreferrer"
          className="block aspect-square hover:opacity-90 transition-opacity bg-gray-100 rounded-lg"
        >
          <img 
            src={release.image_url} 
            alt={release.title} 
            loading="lazy"
            className="w-full h-full object-cover rounded-lg shadow-md"
          />
        </a>
        <button
          onClick={handleToggle}
          disabled={isLoading || !release.id}
          className={`absolute bottom-2 right-2 p-1.5 sm:p-2 rounded-full ${
            isListened ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-800/80 hover:bg-gray-700/80'
          } text-white transition-colors ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title={isListened ? 'Listened' : 'Mark as listened'}
        >
          {isLoading ? (
            <svg className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : isListened ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>
      <div className="px-1 mt-2 sm:mt-3">
        <h3 className="font-medium text-xs sm:text-sm truncate" title={release.title}>
          {release.title}
        </h3>
        <p className="text-gray-600 text-xs sm:text-sm truncate" title={release.artist}>
          {release.artist}
        </p>
        <p className="text-gray-500 text-xs mb-1 sm:mb-2">
          {new Date(release.release_date + 'T12:00:00Z').toLocaleDateString()}
        </p>
        <div className="flex flex-wrap gap-0.5 sm:gap-1">
          {(release.genres || []).map((genre, index) => (
            <span 
              key={`${genre}-${index}`}
              className="bg-violet-200 text-gray-500 px-1.5 sm:px-2 py-0.5 rounded-full text-xs leading-tight"
            >
              {genre}
            </span>
          ))}
        </div>
      </div>
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.release.id === nextProps.release.id &&
    prevProps.labelId === nextProps.labelId &&
    prevProps.labelName === nextProps.labelName
  );
});

export default ReleaseCard; 